import { createRouter, createWebHistory, type RouteLocation, type RouteRecordRaw } from 'vue-router'
import MainLayout from '@/manage/layouts/MainLayout.vue'
import { useUserStore } from '@/manage/stores/user'
import {
  ADMIN_ONLY_ROUTE_META,
  LOGTO_SCOPES,
  ORGANIZATION_ROUTE_META,
  RECRUITING_AI_ROUTE_META,
  hasAdminAccess,
} from '@/manage/constants/access'
import { useOrganizationStore } from '@/manage/stores/organization'
import i18n from '@/i18n'
import { notifyWarning } from '@/utils/error'
import { featureFlags } from '@/shared/config/features'
import { usePostHog } from '@/shared/composables/usePostHog'
import { websiteLinks } from '@/shared/config/links'
import { isChunkLoadError } from '@/shared/composables/useVersionCheck'
import { userApi } from '@/manage/api/user'
import { useJobStore } from '@/manage/stores/job'
import { getAgentEntryRoute } from '@/manage/utils/agentEntry'
// import { checkVersion, isChunkLoadError } from '@/shared/composables/useVersionCheck'

const enableJobs = featureFlags.jobs
const enableEmailCampaign = featureFlags.emailCampaign
const enableContentManagement = featureFlags.contentManagement
const MANAGE_WHITELIST_NAMES = new Set(['Login', 'Callback'])

const ExternalRedirectPlaceholder = { render: () => null }

/**
 * `/manage/jobs/create` is a "new job" entry (step 13): without a job it starts a V2 conversation.
 * With `?jobId=` it follows the same entry rule as every other entry; a job not in the job store yet
 * has no known version and therefore counts as V2.
 */
const resolveJobCreateRedirect = (to: RouteLocation) => {
  const rawJobId = Array.isArray(to.query.jobId) ? to.query.jobId[0] : to.query.jobId
  const jobId = String(rawJobId || '').trim()
  if (!jobId) return getAgentEntryRoute(null, to.query)
  return getAgentEntryRoute(useJobStore().findJobById(jobId) ?? { id: jobId }, to.query)
}
const OFFICIAL_WEBSITE_PATHS = [
  '/pricing',
  '/about',
  '/blog',
  '/blog/:slug',
  '/privacy/rights',
  '/user-center',
  '/user-center/docs',
  '/user-center/docs/:docId',
  '/privacy_policy',
  '/terms',
  '/404',
] as const

const redirectToOfficialWebsite = (to: RouteLocation) => {
  const target = new URL(to.fullPath, `${websiteLinks.officialWebsite}/`)
  window.location.replace(target.toString())
  return false
}

const officialWebsiteRoutes: RouteRecordRaw[] = OFFICIAL_WEBSITE_PATHS.map((path) => ({
  path,
  component: ExternalRedirectPlaceholder,
  beforeEnter: redirectToOfficialWebsite,
}))

const redactReferralUrl = (href: string) => {
  try {
    const url = new URL(href)
    if (/^\/invite\/[^/]+\/?$/.test(url.pathname)) {
      url.pathname = '/invite/[redacted]'
      url.search = ''
      url.hash = ''
      return url.toString()
    }
    const redirect = url.searchParams.get('redirect')
    if (redirect && /^\/invite\/[^/?#]+/.test(redirect)) {
      url.searchParams.set('redirect', '/invite/[redacted]')
    }
    return url.toString()
  } catch {
    return href.replace(/\/invite\/[^/?#]+/g, '/invite/[redacted]')
  }
}

// 路由配置
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/manage',
    },
    ...officialWebsiteRoutes,
    {
      path: '/manage',
      component: MainLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          redirect: '/manage/workbench',
        },
        {
          path: 'workbench',
          name: 'Workbench',
          component: () => import('@/manage/views/workbench/WorkbenchView.vue'),
          meta: { title: 'route.workbench' },
        },
        {
          path: 'admin/resume-import-monitor',
          name: 'ResumeImportMonitor',
          component: () => import('@/manage/views/admin/ResumeImportMonitorView.vue'),
          meta: { title: 'route.resumeImportMonitor', ...ADMIN_ONLY_ROUTE_META },
        },
        {
          path: 'admin/organizations',
          name: 'AdminOrganizations',
          component: () => import('@/manage/views/admin/OrganizationManageView.vue'),
          meta: { title: 'route.adminOrganizations', ...ADMIN_ONLY_ROUTE_META },
        },
        {
          path: 'organization',
          name: 'OrganizationManage',
          component: () => import('@/manage/views/organization/OrganizationView.vue'),
          meta: { title: 'route.organization', ...ORGANIZATION_ROUTE_META },
        },
        {
          path: 'points-record',
          name: 'PointsRecord',
          redirect: {
            path: '/manage/content-management',
            query: { module: 'points' },
          },
          meta: { title: 'route.pointsRecord' },
        },
        {
          path: 'settings/profile',
          name: 'SettingsProfile',
          component: () => import('@/manage/views/settings/ProfileSettingsView.vue'),
          meta: { title: 'route.settingsProfile' },
        },
        // 报表中心已下架，路由暂时保留注释，所有环境都不注册。
        // ...(enableReportCenter
        //   ? [
        //       {
        //         path: 'report-center',
        //         name: 'ReportCenter',
        //         component: () => import('@/manage/views/reports/ReportCenterView.vue'),
        //         meta: { title: 'route.reportCenter', ...ADMIN_ONLY_ROUTE_META },
        //       },
        //     ]
        //   : []),
        ...(enableContentManagement
          ? [
              {
                path: 'content-management',
                name: 'ContentManagement',
                component: () => import('@/manage/views/content/ContentManagementView.vue'),
                meta: { title: 'route.contentManagement' },
              },
            ]
          : []),
        ...(enableEmailCampaign
          ? [
              {
                path: 'email-config',
                name: 'EmailConfig',
                redirect: {
                  path: '/manage/content-management',
                  query: { module: 'email' },
                },
              },
              {
                path: 'inbox',
                name: 'Inbox',
                component: () => import('@/manage/views/email/InboxView.vue'),
                meta: { title: 'route.inbox' },
              },
              {
                path: 'cold-email',
                name: 'ColdEmailCampaign',
                component: () => import('@/manage/views/email/ColdEmailCampaignView.vue'),
                meta: { title: 'route.coldEmail' },
              },
            ]
          : []),
        {
          path: 'talent-pool/apollo-import',
          name: 'ApolloContactImport',
          component: () => import('@/manage/views/talent/ApolloContactImportView.vue'),
          meta: {
            title: 'talent.apolloImportTitle',
            parent: 'route.talentGroup',
            ...ADMIN_ONLY_ROUTE_META,
          },
        },
        {
          path: 'talent-pool',
          name: 'TalentPool',
          component: () => import('@/manage/views/talent/TalentPoolView.vue'),
          meta: { title: 'route.allTalents', parent: 'route.talentGroup' },
        },
        {
          path: 'recruiting-ai',
          name: 'RecruitingAi',
          component: () => import('@/manage/views/recruiting-ai/RecruitingAiView.vue'),
          meta: {
            title: 'route.recruitingAi',
            ...RECRUITING_AI_ROUTE_META,
          },
        },
        {
          path: 'recruiting-ai-v2',
          name: 'RecruitingAiV2',
          component: () => import('@/manage/views/recruiting-ai-v2/RecruitingAiV2View.vue'),
          meta: {
            title: 'route.recruitingAiV2',
            ...RECRUITING_AI_ROUTE_META,
          },
        },
        {
          path: 'recommendation-report-preview',
          name: 'RecommendationReportPreview',
          component: () => import('@/manage/views/reports/RecommendationReportPreviewView.vue'),
          meta: { title: 'route.recommendationReportPreview' },
        },
        ...(enableJobs
          ? [
              {
                path: 'jobs',
                name: 'Jobs',
                component: () => import('@/manage/views/jobs/JobsView.vue'),
                meta: { title: 'route.jobList', parent: 'route.jobsGroup' },
              },
              {
                path: 'jobs/create',
                name: 'JobCreate',
                redirect: (to: RouteLocation) => resolveJobCreateRedirect(to),
                meta: { title: 'route.jobCreate', parent: 'route.jobsGroup' },
              },
              {
                path: 'jobs/:jobId',
                name: 'JobCandidates',
                component: () => import('@/manage/views/jobs/JobCandidatesView.vue'),
                meta: { title: 'route.jobCandidates', parent: 'route.jobsGroup' },
              },
              {
                path: 'jobs/parse',
                name: 'ResumeParse',
                component: () => import('@/manage/views/jobs/ResumeParseView.vue'),
                meta: { title: 'route.resumeParse', parent: 'route.jobsGroup' },
              },
            ]
          : []),
      ],
    },
    {
      path: '/invite/:code',
      name: 'ReferralInvite',
      component: () => import('@/manage/views/referral/ReferralInviteView.vue'),
      meta: { title: 'route.referralInvite' },
    },
    {
      path: '/manage/pricing',
      name: 'ManagePricing',
      component: () => import('@/manage/views/pricing/PricingView.vue'),
      meta: { title: 'route.pricing', requiresAuth: true },
    },
    {
      path: '/manage/referral',
      name: 'ManageReferral',
      component: () => import('@/manage/views/referral/ReferralManageView.vue'),
      meta: { title: 'route.referral', requiresAuth: true },
    },
    {
      path: '/new-user-gift/:giftCode',
      name: 'NewUserGift',
      component: () => import('@/manage/views/new-user-gift/NewUserGiftView.vue'),
      meta: { title: 'route.newUserGift' },
    },
    {
      path: '/manage/callback',
      name: 'Callback',
      component: () => import('@/manage/views/auth/CallbackView.vue'),
      meta: { title: 'route.loginCallback' },
    },
    {
      path: '/manage/login',
      name: 'Login',
      component: () => import('@/manage/views/auth/LoginView.vue'),
      meta: { title: 'route.loggingIn' },
    },
    {
      path: '/login',
      redirect: '/manage/login',
    },
    {
      path: '/callback',
      redirect: '/manage/callback',
    },
    {
      path: '/workbench',
      redirect: '/manage/workbench',
    },
    {
      path: '/talent-pool',
      redirect: (to: RouteLocation) => ({
        path: '/manage/talent-pool',
        query: to.query,
      }),
    },
    {
      path: '/recruiting-ai',
      redirect: (to: RouteLocation) => ({
        path: '/manage/recruiting-ai',
        query: to.query,
      }),
    },
    ...(enableJobs
      ? [
          {
            path: '/jobs',
            redirect: (to: RouteLocation) => ({
              path: '/manage/jobs',
              query: to.query,
            }),
          },
          {
            path: '/jobs/create',
            redirect: (to: RouteLocation) => ({
              path: '/manage/jobs/create',
              query: to.query,
            }),
          },
        ]
      : [
          {
            path: '/jobs',
            redirect: '/manage/workbench',
          },
        ]),
    {
      path: '/:pathMatch(.*)*',
      redirect: '/manage',
    },
  ],
})

router.afterEach((_to, from) => {
  if (typeof document === 'undefined') return
  // 路由切换时顺便检测版本(throttle 10 分钟,见 useVersionCheck)
  // void checkVersion()
  const { posthog, hasPostHogKey } = usePostHog()
  if (!hasPostHogKey || !posthog) return
  // 跳过重定向产生的页面浏览（避免 /login → /manage/login 重复计数）
  // from.matched.length === 0 说明是首次加载或重定向
  const isRedirect = from.matched.length === 0 && from.path !== '/' && from.path !== ''
  if (isRedirect) return
  posthog.capture('$pageview', { $current_url: redactReferralUrl(window.location.href) })
})

// chunk load 失败兜底:新版本上线后旧 hash chunk 文件被清,
// 用户切路由触发 dynamic import 时会报错,此时已无法继续使用,直接 reload。
router.onError((error) => {
  if (!isChunkLoadError(error)) return
  // 防御性:用 sessionStorage 标记一次,避免极端场景下 reload 又失败导致死循环
  const RELOAD_FLAG = 'eh:chunk-reload-attempt'
  if (typeof window === 'undefined') return
  if (window.sessionStorage.getItem(RELOAD_FLAG)) return
  window.sessionStorage.setItem(RELOAD_FLAG, '1')
  window.location.reload()
})

// 路由切换成功后清除 reload 标记,允许下一次出问题时再尝试
router.afterEach(() => {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem('eh:chunk-reload-attempt')
})

/**
 * 检查用户是否需要 AI 招聘引导
 * 判断标准:岗位数量 ≤ 3
 */
async function shouldShowJobCreate(): Promise<boolean> {
  try {
    const jobsData = await userApi.getUserJobs()
    return jobsData.jobs.length <= 3
  } catch (error) {
    console.error('[Router] 检查岗位数量失败:', error)
    // API 失败时默认不重定向(安全策略)
    return false
  }
}

// 全局路由守卫
// 检查用户是否登录，未登录则跳转到登录页
router.beforeEach(async (to, from) => {
  const userStore = useUserStore()
  const isManageRoute = to.matched.some((record) => Boolean(record.meta?.requiresAuth))

  if (!isManageRoute) return true

  // 排除登录页和回调页，避免死循环
  if (to.name && MANAGE_WHITELIST_NAMES.has(String(to.name))) return true

  if (!enableJobs && to.path.startsWith('/manage/jobs')) {
    return '/manage/workbench'
  }

  if (!featureFlags.emailCampaign && to.path.startsWith('/manage/email-config')) {
    return '/manage/workbench'
  }

  if (!enableContentManagement && to.path.startsWith('/manage/content-management')) {
    return '/manage/workbench'
  }

  // Fast-path for in-app navigation: if session is already ready and authenticated,
  // avoid blocking route transition on token/user refresh network calls.
  const hasWarmSession = Boolean(userStore.sessionReady) && Boolean(userStore.isAuthenticated)
  if (!hasWarmSession) {
    const hasSession = await userStore.ensureSession()
    if (!hasSession) {
      return `/manage/login?redirect=${encodeURIComponent(to.fullPath)}`
    }
  } else {
    // Keep session healthy in background without delaying UI navigation.
    void userStore.ensureSession()
  }

  // 🎯 AI 招聘引导:拦截工作台入口,重定向到 AI 招聘页面
  // 仅在用户主动访问工作台且来源不是后台内部导航时检查
  if (to.path === '/manage/workbench' && enableJobs) {
    // 插件可以通过 ?force=true 强制进入工作台(兜底)
    if (to.query.force === 'true') {
      return true
    }

    // 只有从外部进入(初次登录或刷新页面)才检查,避免后台内部导航触发
    const isExternalNavigation =
      !from.path || from.path === '/' || from.name === 'Login' || from.name === 'Callback'
    if (isExternalNavigation) {
      // 检查岗位数量 ≤ 3 时,引导到 AI 招聘页面
      const needsJobCreateGuide = await shouldShowJobCreate()
      if (needsJobCreateGuide) {
        // Step 13: a new conversation always starts on the V2 page.
        return getAgentEntryRoute(null)
      }
    }
  }

  const isEmailConfigRoute = to.path.startsWith('/manage/email-config')
  const isContentManagementRoute = to.path.startsWith('/manage/content-management')
  const isColdEmailRoute = to.path.startsWith('/manage/cold-email')
  const isInboxRoute = to.path.startsWith('/manage/inbox')
  const isRecruitingAiRoute = Boolean(to.meta.recruitingAiOnly)
  const requestedEmailModule =
    isContentManagementRoute &&
    (String(to.query.module || '').trim() === 'email' ||
      String(to.query.module || '').trim() === 'email-config')
  const requestedEmailTemplate =
    isContentManagementRoute && String(to.query.section || '').trim() === 'template'
  const requestedEmailConfigCreate =
    isContentManagementRoute &&
    requestedEmailModule &&
    String(to.query.action || '').trim() === 'create'

  if (
    isEmailConfigRoute ||
    isColdEmailRoute ||
    isInboxRoute ||
    requestedEmailModule ||
    requestedEmailTemplate ||
    requestedEmailConfigCreate
  ) {
    await userStore.forceRefreshDefaultToken()
  }

  if (isRecruitingAiRoute) {
    await userStore.forceRefreshDefaultToken()
  }

  if (
    (isEmailConfigRoute ||
      requestedEmailModule ||
      requestedEmailTemplate ||
      requestedEmailConfigCreate) &&
    !userStore.hasScope(LOGTO_SCOPES.emailManage)
  ) {
    notifyWarning(i18n.global.t('error.noPermission'))
    return '/manage/content-management'
  }

  if (isColdEmailRoute && !userStore.hasScope(LOGTO_SCOPES.emailOutreach)) {
    notifyWarning(i18n.global.t('error.noPermission'))
    return '/manage/workbench'
  }

  if (isInboxRoute && !userStore.hasScope(LOGTO_SCOPES.emailInbox)) {
    notifyWarning(i18n.global.t('error.noPermission'))
    return '/manage/workbench'
  }

  if (to.meta.adminOnly && !hasAdminAccess(userStore.canAdminRead)) {
    notifyWarning(i18n.global.t('error.noPermission'))
    return '/manage/workbench'
  }

  const isOrganizationRoute =
    Boolean(to.meta.requiresOrganization) || to.path.startsWith('/manage/organization')
  if (isOrganizationRoute) {
    if (!userStore.asset) {
      await userStore.fetchAsset()
    }
    let hasOrg = Boolean(userStore.asset?.is_enterprise_mode || userStore.asset?.organization)
    if (!hasOrg) {
      const orgStore = useOrganizationStore()
      const orgData = await orgStore.fetchCurrentOrg()
      if (orgData?.has_organization) {
        hasOrg = true
        void userStore.fetchAsset(true)
      }
    }
    if (!hasOrg) {
      notifyWarning(i18n.global.t('organization.noOrgAccess'))
      return '/manage/workbench'
    }
  }

  return true
})

export default router
